import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { productService } from "../services/product.service";

// Async thunks
export const fetchProducts = createAsyncThunk(
  "product/fetchProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await productService.getProducts();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des produits",
      );
    }
  },
);

export const fetchActiveProducts = createAsyncThunk(
  "product/fetchActiveProducts",
  async (_, { rejectWithValue }) => {
    try {
      return await productService.getActiveProducts();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des produits actifs",
      );
    }
  },
);

export const fetchForSaleProducts = createAsyncThunk(
  "product/fetchForSaleProducts",
  async (_, { rejectWithValue }) => {
    try {
      return await productService.getForSaleProducts();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des produits actifs",
      );
    }
  },
);

export const fetchProductById = createAsyncThunk(
  "product/fetchProductById",
  async (id, { rejectWithValue }) => {
    try {
      return await productService.getProductById(id);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération du produit",
      );
    }
  },
);

export const searchProducts = createAsyncThunk(
  "product/searchProducts",
  async (query, { rejectWithValue }) => {
    try {
      return await productService.searchProducts(query);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la recherche de produits",
      );
    }
  },
);

export const addProduct = createAsyncThunk(
  "product/addProduct",
  async (product, { rejectWithValue }) => {
    try {
      const result = await productService.createProduct(product);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la création du produit",
      );
    }
  },
);

export const editProduct = createAsyncThunk(
  "product/editProduct",
  async ({ id, product }, { rejectWithValue }) => {
    try {
      await productService.updateProduct(id, product);
      return { id, ...product };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la mise à jour du produit",
      );
    }
  },
);

export const removeProduct = createAsyncThunk(
  "product/removeProduct",
  async (id, { rejectWithValue }) => {
    try {
      await productService.deleteProduct(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la suppression du produit",
      );
    }
  },
);


export const importProducts = createAsyncThunk(
  "product/importProducts",
  async (file, { rejectWithValue, dispatch }) => { // <-- Added 'dispatch' here
    try {
      const result = await productService.importProducts(file);
      
      // Automatically refresh the products list after a successful import
      dispatch(fetchProducts()); 
      
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de l'importation des produits",
      );
    }
  },
);

export const fetchInactiveProducts = createAsyncThunk(
  "product/fetchInactiveProducts",
  async (_, { rejectWithValue }) => {
    try {
      return await productService.getInactiveProducts();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des produits inactifs",
      );
    }
  },
);

export const restoreProduct = createAsyncThunk(
  "product/restoreProduct",
  async (id, { rejectWithValue }) => {
    try {
      await productService.restoreProduct(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la restauration du produit",
      );
    }
  },
);

export const pushSalePrices = createAsyncThunk(
  "product/pushSalePrices",
  async (_, { rejectWithValue }) => {
    try {
      return await productService.pushSalePrices();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la synchronisation des prix",
      );
    }
  },
);
export const fetchLightProducts = createAsyncThunk(
  "product/fetchLightProducts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await productService.getLightProducts();
      return response; // not response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error fetching light products",
      );
    }
  },
);

export const fetchSummaryProducts = createAsyncThunk(
  "product/fetchSummaryProducts",
  async (_, { rejectWithValue }) => {
    try {
      return await productService.getSummaryProducts();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error fetching product summaries",
      );
    }
  },
);
export const fetchSummaryComposedProducts = createAsyncThunk(
  "product/fetchSummaryComposedProducts",
  async (_, { rejectWithValue }) => {
    try {
      return await productService.getSummaryComposedProducts();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error fetching product summaries",
      );
    }
  },
);

// Initial state
const initialState = {
  products: [],
  inactiveProducts: [],
  totalCount: 0,
  inactiveTotalCount: 0,
  searchResults: [],
  selectedProduct: null,
  syncPricesLoading: false,
  loading: false,
  inactiveLoading: false,
  searchLoading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
  searchQuery: "",
  lightProducts: [],
  lightLoading: false,
  summaryProducts: [],
  summaryLoading: false,
  summaryComposedProducts: [],
  summaryComposedLoading: false,
};

// Slice
const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch active products
      .addCase(fetchActiveProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchActiveProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch For sale products
      .addCase(fetchForSaleProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchForSaleProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchForSaleProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search products
      .addCase(searchProducts.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })

      // Add product
      .addCase(addProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products.push(action.payload);
        state.showModal = false;
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit product
      .addCase(editProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editProduct.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex(
          (product) => product.productId === action.payload.id,
        );
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        state.showModal = false;
      })
      .addCase(editProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove product
      .addCase(removeProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter(
          (product) => product.id !== action.payload,
        );
        state.showDeleteModal = false;
      })
      .addCase(removeProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

            // Import products
      .addCase(importProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importProducts.fulfilled, (state) => {
        state.loading = false;
        // List is automatically refreshed by the dispatch(fetchProducts()) in the thunk above
      })
      .addCase(importProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchInactiveProducts.pending, (state) => {
        state.inactiveLoading = true;
        state.error = null;
      })
      .addCase(fetchInactiveProducts.fulfilled, (state, action) => {
        state.inactiveLoading = false;
        state.inactiveProducts = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];
        state.inactiveTotalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchInactiveProducts.rejected, (state, action) => {
        state.inactiveLoading = false;
        state.error = action.payload;
      })

      .addCase(restoreProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.inactiveProducts = state.inactiveProducts.filter(
          (product) => product.id !== action.payload,
        );
      })
      .addCase(restoreProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(pushSalePrices.pending, (state) => {
        state.syncPricesLoading = true;
        state.error = null;
      })
      .addCase(pushSalePrices.fulfilled, (state) => {
        state.syncPricesLoading = false;
      })
      .addCase(pushSalePrices.rejected, (state, action) => {
        state.syncPricesLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLightProducts.pending, (state) => {
        state.lightLoading = true;
      })
      .addCase(fetchLightProducts.fulfilled, (state, action) => {
        state.lightLoading = false;
        state.lightProducts = action.payload;
      })
      .addCase(fetchLightProducts.rejected, (state, action) => {
        state.lightLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSummaryProducts.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchSummaryProducts.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summaryProducts = action.payload;
      })
      .addCase(fetchSummaryProducts.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSummaryComposedProducts.pending, (state) => {
        state.summaryComposedLoading = true;
      })
      .addCase(fetchSummaryComposedProducts.fulfilled, (state, action) => {
        state.summaryComposedLoading = false;
        state.summaryComposedProducts = action.payload;
      })
      .addCase(fetchSummaryComposedProducts.rejected, (state, action) => {
        state.summaryComposedLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedProduct,
  setSearchQuery,
  clearSearchResults,
} = productSlice.actions;

export default productSlice.reducer;